/**
 * Minimal structural shape these helpers need — keeps them usable from both
 * `JsonSchema` (data/types) and the diff viewer's local `SchemaObj`.
 */
interface TypedSchema {
  type?: string | string[];
  properties?: Record<string, unknown>;
  items?: unknown;
}

/**
 * Normalise `type` to a list of string members.
 *
 * Specs are stored as-authored with no schema validation, so `type` can be a
 * string, a union array (OpenAPI 3.1 nullable fields), or — for a malformed or
 * hostile spec — something else entirely. Anything that is not a string is
 * dropped here rather than leaking onward: a non-string reaching a React child
 * or a `.endsWith()` call takes the whole docs page down, which is the failure
 * this module exists to prevent.
 */
function typeMembers(schema: TypedSchema | null | undefined): string[] {
  const t = schema?.type;
  if (Array.isArray(t)) return t.filter((m): m is string => typeof m === "string");
  return typeof t === "string" ? [t] : [];
}

/**
 * The single type callers branch on.
 *
 * Union members are unordered in JSON Schema, so we cannot just take the first:
 * `["string","object"]` and `["object","string"]` must behave identically.
 * Prefer the member the schema's own body evidences — `properties` means
 * object, `items` means array — and otherwise take the first non-null member.
 * `["number","null"]` therefore behaves like `"number"`.
 */
export function schemaType(schema: TypedSchema | null | undefined): string | undefined {
  const members = typeMembers(schema);
  if (members.length === 0) return undefined;
  if (members.includes("object") && schema?.properties) return "object";
  if (members.includes("array") && schema?.items) return "array";
  return members.find((m) => m !== "null") ?? members[0];
}

/**
 * Display form of a schema's own type — keeps the union visible so the docs
 * stay honest: `"string"`, `"number | null"`, or undefined when untyped.
 */
export function schemaTypeName(schema: TypedSchema | null | undefined): string | undefined {
  const members = typeMembers(schema);
  return members.length > 0 ? members.join(" | ") : undefined;
}

/** True when the schema's type union admits `null`. */
export function isNullableSchema(schema: TypedSchema | null | undefined): boolean {
  return typeMembers(schema).includes("null");
}

/**
 * Full display label, including the `[]` suffix for arrays.
 *
 * A union element type is parenthesised so a nullable array of nullable
 * strings reads `(string | null)[] | null` rather than the ambiguous
 * `string | null[]`, which reads as "a string, or an array of nulls".
 */
export function schemaTypeLabel(
  schema: (TypedSchema & { items?: TypedSchema | null }) | null | undefined,
  fallback = "any"
): string {
  if (schemaType(schema) === "array" && schema?.items) {
    const inner = schemaTypeName(schema.items) ?? fallback;
    const element = inner.includes(" | ") ? `(${inner})[]` : `${inner}[]`;
    return isNullableSchema(schema) ? `${element} | null` : element;
  }
  return schemaTypeName(schema) ?? fallback;
}
